/**
 * Iter 67ba — Vendor invoices index.
 *
 * Mounted inside the Vendor Dashboard as a tab. Lists all invoices the vendor
 * has created, with a one-click "Create from booking" shortcut for any
 * confirmed booking that doesn't yet have an invoice.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Plus, Receipt, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Money from "@/components/Money";
import InvoiceDeliveryActions from "@/components/invoices/InvoiceDeliveryActions";
import AttributionChips from "@/components/AttributionChips";

const STATUS_TONES = {
  draft:     "bg-neutral-100 text-neutral-700",
  issued:    "bg-blue-50 text-blue-700",
  adjusted:  "bg-amber-50 text-amber-800",
  settled:   "bg-emerald-50 text-emerald-700",
  cancelled: "bg-rose-50 text-rose-700",
};

export default function VendorInvoicesTab() {
  const [invoices, setInvoices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [invR, bkR] = await Promise.all([
        api.get("/vendor/invoices?limit=200"),
        api.get("/vendor/calendar?days=180").catch(() => ({ data: { bookings: [] } })),
      ]);
      setInvoices(invR.data.items || []);
      // Calendar payload exposes confirmed bookings — only need a thin list
      // for the "create from booking" picker. We don't fail if calendar 404s.
      setBookings(bkR.data?.bookings || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Could not load invoices.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Bookings that don't already have an invoice — these get the shortcut.
  const bookingIdsWithInvoice = new Set(invoices.map((inv) => inv.booking_id));
  const eligibleBookings = bookings.filter(
    (b) => b.status === "confirmed" && !bookingIdsWithInvoice.has(b.id),
  ).slice(0, 6);

  const onCreateFromBooking = async (booking_id) => {
    try {
      const r = await api.post(`/invoices/from-booking/${booking_id}`);
      toast.success(`Invoice ${r.data.invoice_number} created.`);
      window.location.href = `/vendor/invoices/${r.data.id}`;
    } catch (err) {
      toast.error(formatApiError(err) || "Could not create invoice.");
    }
  };

  return (
    <div className="space-y-6" data-testid="vendor-invoices-tab">
      <div>
        <h2 className="font-display text-2xl font-medium">Invoices</h2>
        <p className="text-sm text-neutral-600 mt-1">
          Auto-seeded from confirmed bookings. Add tolls, fuel, cleaning, or damage charges before issuing to the customer.
        </p>
      </div>

      {eligibleBookings.length > 0 && (
        <div className="bg-gradient-to-br from-indigo-50 via-white to-violet-50/40 rounded-2xl border border-indigo-100 p-5" data-testid="invoices-quick-create">
          <div className="text-[10px] uppercase tracking-wider font-bold text-indigo-900 mb-2">Bookings without an invoice</div>
          <div className="flex gap-2 flex-wrap">
            {eligibleBookings.map((b) => (
              <Button
                key={b.id}
                variant="outline"
                size="sm"
                className="rounded-full text-xs"
                onClick={() => onCreateFromBooking(b.id)}
                data-testid={`invoices-create-from-${b.id}`}
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                {b.car_label || b.id.slice(0, 8)} · {b.start_date?.slice(0, 10)}
              </Button>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-border text-[10px] uppercase tracking-wider text-neutral-600">
              <tr>
                <th className="text-left px-5 py-3">Number</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Issued</th>
                <th className="text-right px-5 py-3">Total</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-neutral-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</td></tr>
              )}
              {!loading && invoices.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-neutral-500">
                  <Receipt className="h-7 w-7 mx-auto mb-2 text-neutral-300" />
                  No invoices yet. Create one from any confirmed booking above.
                </td></tr>
              )}
              {!loading && invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-border last:border-0 hover:bg-neutral-50/60" data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-5 py-3.5">
                    <div className="font-semibold text-neutral-900">{inv.invoice_number}</div>
                    <div className="text-xs text-neutral-500">Booking {inv.booking_id?.slice(0, 8)}</div>
                    <AttributionChips entity={inv} actions={["create", "update", "approve"]} compact className="mt-1" />
                  </td>
                  <td className="px-5 py-3.5">
                    <Badge variant="outline" className={`uppercase tracking-wider text-[10px] font-bold ${STATUS_TONES[inv.status] || STATUS_TONES.draft}`}>
                      {inv.status}
                    </Badge>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-neutral-700">{inv.issued_at?.slice(0, 10) || "—"}</td>
                  <td className="px-5 py-3.5 text-right">
                    <Money amount={inv.totals?.total_gross || 0} className="font-semibold text-neutral-900" />
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {inv.status !== "draft" && (
                        <InvoiceDeliveryActions
                          kind="rental"
                          invoiceId={inv.id}
                          invoiceNumber={inv.invoice_number}
                          variant="icons"
                        />
                      )}
                      <Link to={`/vendor/invoices/${inv.id}`}>
                        <Button variant="ghost" size="sm" className="text-indigo-600 hover:text-indigo-900" data-testid={`invoice-open-${inv.id}`}>
                          <FileText className="h-3.5 w-3.5 mr-1" /> Open
                        </Button>
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
